import {
  BUY,
  CASH_APPROACH,
  RISK_WEIGHTED_APPROACH,
  selectors,
  SELL,
  SHORT_SELL,
  utils,
} from 'state';
import { useSelector } from 'react-redux';
import usePrices from './usePrices';
import * as util from 'util';
import { remainingReportingAbsoluteEquityLeverage } from '../../../state/app/selectors';
import { eq } from 'lodash';

const useMaxQuantity = (
  instrumentId: number,
  transaction: number,
  limitPrice?: number,
  orderType?: string,
) => {
  const session = useSelector(selectors.activeSession);
  const {
    fx_leverage,
    bond_leverage,
    equity_leverage,
    etf_equities_leverage,
    etf_commodities_leverage,
    futures_leverage,
    risk_weighted_limit,
    risk_approach,
    starting_cash_amount,
    reporting_currency,
    limit_order_collateral,
    hard_limits,
  } = session;

  const instrument = useSelector(
    selectors.financialInstrumentById(instrumentId),
  );
  const asset = useSelector(selectors.underlyingAssetOfInstrument(instrument));
  const player = useSelector(selectors.player);

  const assetList = useSelector(selectors.underlyingAssetsList);
  const currencyList = useSelector(selectors.currenciesList);

  const assetIsBond = asset.resourcetype.endsWith('Bond');
  const assetIsEquity = asset.resourcetype === 'Equity';
  const assetIsETFEquities = asset.resourcetype === 'ETFEquities';
  const assetIsETFCommodities = asset.resourcetype === 'ETFCommodities';
  const assetIsFutures = asset.resourcetype === 'Futures';
  let { price, reportingPrice } = usePrices(instrumentId, transaction);
  if (orderType === 'LimitOrder') {
    if (limitPrice !== undefined) price = limitPrice;
    if (assetIsBond) price /= 100;
    reportingPrice = utils.cashExchange(
      asset.base_currency,
      session.reporting_currency,
      price,
      0,
      assetList,
      currencyList,
    );
  }
  const remainingReportingAbsoluteBondLeverage = useSelector(
    // already divided by 100 in backend
    selectors.remainingReportingAbsoluteBondLeverage,
  );

  const remainingReportingAbsoluteEquityLeverage = useSelector(
    selectors.remainingReportingAbsoluteEquityLeverage,
  );

  const remainingReportingAbsoluteETFEquitiesLeverage = useSelector(
    selectors.remainingReportingAbsoluteETFEquitiesLeverage,
  );

  const remainingReportingAbsoluteETFCommoditiesLeverage = useSelector(
    selectors.remainingReportingAbsoluteETFCommoditiesLeverage,
  );

  const remainingReportingAbsoluteFuturesLeverage = useSelector(
    selectors.remainingReportingAbsoluteFuturesLeverage,
  );

  if (!instrument || !price) {
    // return 0;
    return {
      maxQuantityHardLimits: 0,
      maxQuantitySoftLimits: 0,
    };
  }

  const wallet = player.wallets.find(
    wallet => asset.variable_currency === wallet.currency,
  );
  const reportingCurrency = currencyList.find(c => c.id === reporting_currency);
  if (!wallet || !reportingCurrency) {
    // return 0;
    return {
      maxQuantityHardLimits: 0,
      maxQuantitySoftLimits: 0,
    };
  }

  const reporting_avg_open = utils.cashExchange(
    asset.base_currency,
    reportingCurrency.symbol,
    instrument.avg_open_price,
    0,
    assetList,
    currencyList,
  );
  const reporting_wallet_amount = utils.cashExchange(
    wallet.currency,
    reportingCurrency.symbol,
    wallet.amount,
    0,
    assetList,
    currencyList,
  );

  let maxQuantity = 0;
  let maxQuantitySoftLimits = 0;
  // SELL
  if (transaction === SELL) {
    maxQuantity =
      instrument.amount > 0 ? instrument.amount - instrument.repoed_amount : 0;
    maxQuantitySoftLimits = maxQuantity;
  }
  // BUY
  else if (transaction === BUY && instrument.amount < 0) {
    maxQuantity = -instrument.amount;
    maxQuantitySoftLimits = maxQuantity;
  }

  // CASH APPROACH
  else if (risk_approach === CASH_APPROACH) {
    let reportingAssetLimit = starting_cash_amount * instrument.pv_limit;
    let reportingAssetSoftLimit = starting_cash_amount;

    // FX
    if (asset.resourcetype === 'FX') {
      maxQuantity = Math.min(
        player.remaining_fx_amount_limit,
        fx_leverage * starting_cash_amount,
      );
    } else if (asset.resourcetype === 'FxForwards') {
      //
      // maxQuantity = Math.min(
      //     player.remaining_reporting_absolute_hedge_amount,
      //     instrumentLimit,
      // )
    } else if (transaction === BUY) {
      // BUY
      let reportingAssetValue = Math.max(
        0,
        instrument.amount * reporting_avg_open,
      );

      const remainingValue = Math.max(
        0,
        Math.min(
          reportingAssetLimit - reportingAssetValue,
          reporting_wallet_amount,
        ),
      );
      const reminaingValueSoftLimit = Math.min(
        reportingAssetSoftLimit - reportingAssetValue,
        reporting_wallet_amount,
      );

      maxQuantity = remainingValue / reportingPrice;
      maxQuantitySoftLimits = reminaingValueSoftLimit / reportingPrice;

      if (assetIsBond) {
        reportingAssetLimit = reportingAssetLimit / bond_leverage;
        reportingAssetSoftLimit = reportingAssetSoftLimit / bond_leverage;
        reportingAssetValue /= 100;

        const remainingReportingLimit = Math.max(
          0,
          reportingAssetLimit - reportingAssetValue,
        );
        const remainingReportingSoftLimit =
          reportingAssetSoftLimit - reportingAssetValue;
        const maxByCollateral = reporting_wallet_amount / bond_leverage;
        const min = Math.min(
          remainingReportingLimit,
          maxByCollateral,
          remainingReportingAbsoluteBondLeverage,
        );
        const minSoftLimit = Math.min(
          remainingReportingSoftLimit,
          maxByCollateral,
          remainingReportingAbsoluteBondLeverage,
        );
        maxQuantity = min / reportingPrice;
        maxQuantitySoftLimits = minSoftLimit / reportingPrice;
      }

      if (assetIsEquity) {
        reportingAssetLimit = reportingAssetLimit / equity_leverage;
        reportingAssetSoftLimit = reportingAssetSoftLimit / equity_leverage;
        console.log(`reportingAssetLimit: ${reportingAssetLimit}`);
        console.log(`reportingAssetSoftLimit: ${reportingAssetSoftLimit}`);
        console.log(`equity_leverage: ${equity_leverage}`);
        console.log(`reportingAssetValue: ${reportingAssetValue}`);

        const remainingReportingLimit = Math.max(
          0,
          reportingAssetLimit - reportingAssetValue,
        );
        console.log(`remainingReportingLimit: ${remainingReportingLimit}`);

        const remainingReportingSoftLimit =
          reportingAssetSoftLimit - reportingAssetValue;
        console.log(
          `remainingReportingSoftLimit: ${remainingReportingSoftLimit}`,
        );

        const maxByCollateral = reporting_wallet_amount / equity_leverage;
        console.log(`maxByCollateral: ${maxByCollateral}`);
        console.log(
          `remainingReportingAbsoluteEquityLeverage: ${remainingReportingAbsoluteEquityLeverage}`,
        );

        const min = Math.min(
          remainingReportingLimit,
          maxByCollateral,
          remainingReportingAbsoluteEquityLeverage,
        );
        console.log(`min: ${min}`);

        const minSoftLimit = Math.min(
          remainingReportingSoftLimit,
          maxByCollateral,
          remainingReportingAbsoluteEquityLeverage,
        );
        console.log(`minSoftLimit: ${minSoftLimit}`);

        maxQuantity = min / reportingPrice;
        maxQuantitySoftLimits = minSoftLimit / reportingPrice;
        console.log(`maxQuantity: ${maxQuantity}`);
        console.log(`maxQuantitySoftLimits: ${maxQuantitySoftLimits}`);
        console.log(`reportingPrice: ${reportingPrice}`);
      }

      if (assetIsETFEquities) {
        reportingAssetLimit = reportingAssetLimit / etf_equities_leverage;
        reportingAssetSoftLimit =
          reportingAssetSoftLimit / etf_equities_leverage;
        const remainingReportingLimit = Math.max(
          0,
          reportingAssetLimit - reportingAssetValue,
        );
        const remainingReportingSoftLimit =
          reportingAssetSoftLimit - reportingAssetValue;
        const maxByCollateral = reporting_wallet_amount / etf_equities_leverage;
        const min = Math.min(
          remainingReportingLimit,
          maxByCollateral,
          remainingReportingAbsoluteETFEquitiesLeverage,
        );
        const minSoftLimit = Math.min(
          remainingReportingSoftLimit,
          maxByCollateral,
          remainingReportingAbsoluteETFEquitiesLeverage,
        );
        maxQuantity = min / reportingPrice;
        maxQuantitySoftLimits = minSoftLimit / reportingPrice;
      }

      if (assetIsETFCommodities) {
        reportingAssetLimit = reportingAssetLimit / etf_commodities_leverage;
        reportingAssetSoftLimit =
          reportingAssetSoftLimit / etf_commodities_leverage;
        const remainingReportingLimit = Math.max(
          0,
          reportingAssetLimit - reportingAssetValue,
        );
        const remainingReportingSoftLimit =
          reportingAssetSoftLimit - reportingAssetValue;
        const maxByCollateral =
          reporting_wallet_amount / etf_commodities_leverage;
        const min = Math.min(
          remainingReportingLimit,
          maxByCollateral,
          remainingReportingAbsoluteETFCommoditiesLeverage,
        );
        const minSoftLimit = Math.min(
          remainingReportingSoftLimit,
          maxByCollateral,
          remainingReportingAbsoluteETFCommoditiesLeverage,
        );
        maxQuantity = min / reportingPrice;
        maxQuantitySoftLimits = minSoftLimit / reportingPrice;
      }

      if (assetIsFutures) {
        reportingAssetLimit = reportingAssetLimit / futures_leverage;
        reportingAssetSoftLimit = reportingAssetSoftLimit / futures_leverage;

        const remainingReportingLimit = Math.max(
          0,
          reportingAssetLimit - reportingAssetValue,
        );
        const remainingReportingSoftLimit =
          reportingAssetSoftLimit - reportingAssetValue;
        const maxByCollateral = reporting_wallet_amount / futures_leverage;
        const min = Math.min(
          remainingReportingLimit,
          maxByCollateral,
          remainingReportingAbsoluteFuturesLeverage,
        );
        const minSoftLimit = Math.min(
          remainingReportingSoftLimit,
          maxByCollateral,
          remainingReportingAbsoluteFuturesLeverage,
        );
        maxQuantity = min / reportingPrice;
        maxQuantitySoftLimits = minSoftLimit / reportingPrice;
      }

      //SHORT SELL
    } else if (transaction === SHORT_SELL) {
      let reportingAssetValue = instrument.amount * reporting_avg_open;

      const remainingValue = Math.min(
        reportingAssetLimit + reportingAssetValue,
        player.remaining_short_sell_amount_limit,
      );
      const remainingValueSoftLimit = Math.min(
        reportingAssetSoftLimit + reportingAssetValue,
        player.remaining_short_sell_amount_limit,
      );
      maxQuantity = remainingValue / reportingPrice;
      maxQuantitySoftLimits = remainingValueSoftLimit / reportingPrice;
      if (assetIsBond) {
        reportingAssetValue /= 100;
        reportingAssetLimit = reportingAssetLimit / bond_leverage;
        reportingAssetSoftLimit = reportingAssetSoftLimit / bond_leverage;
        const remainingReportingLimit = Math.max(
          0,
          reportingAssetLimit + reportingAssetValue,
        );
        const remainingReportingSoftLimit =
          reportingAssetSoftLimit + reportingAssetValue;
        const maxByCollateral = reporting_wallet_amount / bond_leverage;
        // console.log('reportingAssetLimit: ', reportingAssetLimit);
        // console.log('reportingAssetValue: ', reportingAssetValue);
        const min = Math.min(
          remainingReportingLimit,
          // TODO: maxByCollateral in short sell case was commented bc. it was giving 0 limit if you had 0 amount
          // TODO: in your wallet (which is stupid bc. by short selling you're trying to raise money. Not having money
          // TODO: in your wallet shouldn't prevent you from doing a short.)
          // TODO: this has to be re-evalued so in non-leveraged sessions you're not bound to your wallet
          //  , but in leveraged sessions you're forced to use the Repo mechanism
          // maxByCollateral,
          remainingReportingAbsoluteBondLeverage,
          player.remaining_short_sell_amount_limit / bond_leverage,
        );
        const minSoftLimit = Math.min(
          remainingReportingSoftLimit,
          // maxByCollateral,
          remainingReportingAbsoluteBondLeverage,
          player.remaining_short_sell_amount_limit / bond_leverage,
        );
        maxQuantity = min / reportingPrice;
        maxQuantitySoftLimits = minSoftLimit / reportingPrice;
      }

      if (assetIsEquity) {
        reportingAssetLimit = reportingAssetLimit / equity_leverage;
        reportingAssetSoftLimit = reportingAssetSoftLimit / equity_leverage;
        const remainingReportingLimit = Math.max(
          0,
          reportingAssetLimit + reportingAssetValue,
        );
        const remainingReportingSoftLimit =
          reportingAssetSoftLimit + reportingAssetValue;
        const maxByCollateral = reporting_wallet_amount / equity_leverage;
        const min = Math.min(
          remainingReportingLimit,
          // maxByCollateral,
          remainingReportingAbsoluteEquityLeverage,
          player.remaining_short_sell_amount_limit / equity_leverage,
        );
        const minSoftLimit = Math.min(
          remainingReportingSoftLimit,
          // maxByCollateral,
          remainingReportingAbsoluteEquityLeverage,
          player.remaining_short_sell_amount_limit / equity_leverage,
        );
        maxQuantity = min / reportingPrice;
        maxQuantitySoftLimits = minSoftLimit / reportingPrice;
      }

      if (assetIsETFEquities) {
        reportingAssetLimit = reportingAssetLimit / etf_equities_leverage;
        reportingAssetSoftLimit =
          reportingAssetSoftLimit / etf_equities_leverage;
        const remainingReportingLimit = Math.max(
          0,
          reportingAssetLimit + reportingAssetValue,
        );
        const remainingReportingSoftLimit =
          reportingAssetSoftLimit + reportingAssetValue;
        const min = Math.min(
          remainingReportingLimit,
          remainingReportingAbsoluteETFEquitiesLeverage,
          player.remaining_short_sell_amount_limit / etf_equities_leverage,
        );
        const minSoftLimit = Math.min(
          remainingReportingSoftLimit,
          remainingReportingAbsoluteETFEquitiesLeverage,
          player.remaining_short_sell_amount_limit / etf_equities_leverage,
        );
        maxQuantity = min / reportingPrice;
        maxQuantitySoftLimits = minSoftLimit / reportingPrice;
      }

      if (assetIsETFCommodities) {
        reportingAssetLimit = reportingAssetLimit / etf_commodities_leverage;
        reportingAssetSoftLimit =
          reportingAssetSoftLimit / etf_commodities_leverage;
        const remainingReportingLimit = Math.max(
          0,
          reportingAssetLimit + reportingAssetValue,
        );
        const remainingReportingSoftLimit =
          reportingAssetSoftLimit + reportingAssetValue;
        const min = Math.min(
          remainingReportingLimit,
          remainingReportingAbsoluteETFCommoditiesLeverage,
          player.remaining_short_sell_amount_limit / etf_commodities_leverage,
        );
        const minSoftLimit = Math.min(
          remainingReportingSoftLimit,
          remainingReportingAbsoluteETFCommoditiesLeverage,
          player.remaining_short_sell_amount_limit / etf_commodities_leverage,
        );
        maxQuantity = min / reportingPrice;
        maxQuantitySoftLimits = minSoftLimit / reportingPrice;
      }

      if (assetIsFutures) {
        reportingAssetLimit = reportingAssetLimit / futures_leverage;
        reportingAssetSoftLimit = reportingAssetSoftLimit / futures_leverage;
        const remainingReportingLimit = Math.max(
          0,
          reportingAssetLimit + reportingAssetValue,
        );
        const remainingReportingSoftLimit =
          reportingAssetSoftLimit + reportingAssetValue;
        const min = Math.min(
          remainingReportingLimit,
          remainingReportingAbsoluteFuturesLeverage,
          player.remaining_short_sell_amount_limit / futures_leverage,
        );
        const minSoftLimit = Math.min(
          remainingReportingSoftLimit,
          remainingReportingAbsoluteFuturesLeverage,
          player.remaining_short_sell_amount_limit / futures_leverage,
        );
        maxQuantity = min / reportingPrice;
        maxQuantitySoftLimits = minSoftLimit / reportingPrice;
      }
    }
  }

  // RISK WEIGHT APPROACH
  else if (risk_approach === RISK_WEIGHTED_APPROACH) {
    let remaining =
      // (risk_weighted_limit - player.risk_weighted_value) / inverseExchangeRate;
      risk_weighted_limit - player.risk_weighted_value;

    if (assetIsBond) {
      remaining *= 100;
    }

    maxQuantity =
      (risk_weighted_limit * instrument.pv_limit) /
      reportingPrice /
      instrument.risk_weight;

    maxQuantity -= Math.abs(instrument.amount);

    if (remaining < maxQuantity) {
      maxQuantity = remaining;
    }
  }

  // Formatting
  let maxQuantityHardLimits = Math.floor(maxQuantity || 0);
  if (maxQuantityHardLimits < 0) maxQuantityHardLimits = 0;
  maxQuantitySoftLimits = Math.floor(maxQuantitySoftLimits || 0);
  if (maxQuantitySoftLimits < 0) maxQuantitySoftLimits = 0;

  return { maxQuantityHardLimits, maxQuantitySoftLimits };
};

export default useMaxQuantity;
